import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { isSafeUrl } from '../../../../../../../../utils/utils';

import { CitationSource } from './MessagesContainer.types';
import { CitationAnswerProps } from './MessagesContainer.types';
import remarkCitations from './remarkCitations';
import CitationMarker from './CitationMarker';
import SourcesPanel from './SourcesPanel';
import { buildCanonicalMap, buildDisplayMap, countTableColumns } from './citationUtils';

import styles from './MessagesContainer.module.scss';

/**
 * Renders one assistant answer: the markdown content (copy target) plus the
 * SourcesPanel. Citation canonicalisation is done ONCE here (memoised on the
 * message's sources) and shared by the inline markers, the table footers, and
 * the panel — so duplicate citations of the same source share one number/anchor.
 *
 * Why canonicalisation lives in the FE (not Athena): inline ‹cite:N› markers
 * stream token-by-token before the final `sources` array arrives, so the client
 * is the only place that can reconcile emitted ids → a stable display number.
 */
const CitationAnswer: React.FC<CitationAnswerProps> = ({ message, registerContentRef }) => {
    const sources = message.metadata?.sources;
    const { idToCanonical, canonicalSources } = useMemo(() => buildCanonicalMap(sources), [sources]);
    // Reading-order display numbers: the first reference in the answer is [1].
    // model_knowledge sources get no number and are never surfaced.
    const { displayNumbers, orderedSources } = useMemo(
        () => buildDisplayMap(message.content, idToCanonical, canonicalSources),
        [message.content, idToCanonical, canonicalSources],
    );

    const components = useMemo(
        () => ({
            table: ({ children, ...tableProps }: any) => {
                // Table-source ids injected onto the table node by remarkCitations,
                // canonicalised + deduped so the footer matches the inline markers + panel.
                const tableSourcesRaw = tableProps['data-table-sources'] as string | undefined;
                const tableSourceIds = tableSourcesRaw
                    ? tableSourcesRaw
                          .split(',')
                          .map((n: string) => parseInt(n.trim(), 10))
                          .filter(Boolean)
                    : [];
                const canonicalIds = [...new Set(tableSourceIds.map((tid: number) => idToCanonical[tid] ?? tid))];
                // orderedSources already excludes model_knowledge (reasoning is never
                // grounded evidence) and is sorted by display number.
                const citedSources = orderedSources.filter((s: CitationSource) => canonicalIds.includes(s.id));

                return (
                    <div className={styles.TableWrapper}>
                        <table className={styles.Table}>
                            {children}
                            {citedSources.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td colSpan={countTableColumns(tableProps.node)} className={styles.TableSourcesFooter}>
                                            Sources:{' '}
                                            {citedSources.map((s: CitationSource, idx: number) => (
                                                <span key={s.id}>
                                                    {idx > 0 && ' · '}[{displayNumbers[s.id] ?? s.id}]{' '}
                                                    {'file_name' in s ? s.file_name : s.display_name}
                                                </span>
                                            ))}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                );
            },
            // Branch on data-citation-id: present → CitationMarker, else a plain <sup>.
            sup: ({ children, ...supProps }: any) => {
                const citationId = supProps['data-citation-id'];
                if (citationId !== undefined) {
                    return (
                        <CitationMarker
                            id={Number(citationId)}
                            msgId={message.id}
                            sources={sources}
                            idToCanonical={idToCanonical}
                            displayNumbers={displayNumbers}
                        />
                    );
                }
                return <sup {...supProps}>{children}</sup>;
            },
            code: ({ children, className, ...props }: any) => {
                // react-markdown v10 removed the `inline` prop. Fenced blocks carry a
                // language-* className and/or multi-line content; inline snippets don't.
                const isBlock = /\blanguage-/.test(className ?? '') || String(children).includes('\n');
                return isBlock ? (
                    <pre className={styles.CodeBlock}>
                        <code className={className} {...props}>
                            {children}
                        </code>
                    </pre>
                ) : (
                    <code className={styles.InlineCode} {...props}>
                        {children}
                    </code>
                );
            },
            a: ({ href, children }: any) => {
                const safeHref = href && isSafeUrl(href) ? href : '#';
                if (safeHref === '#' && href) return <></>;
                return (
                    <a
                        href={safeHref}
                        className={styles.Link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={typeof children === 'string' ? children : ''}
                    >
                        <span>{children}</span>
                    </a>
                );
            },
        }),
        [message.id, sources, idToCanonical, displayNumbers, orderedSources],
    );

    return (
        <>
            <div data-content-role="assistant" ref={registerContentRef}>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkCitations]} components={components}>
                    {message.content}
                </ReactMarkdown>
            </div>
            {/* SourcesPanel sits OUTSIDE the copy target so it isn't included in copy */}
            <SourcesPanel msgId={message.id} canonicalSources={orderedSources} displayNumbers={displayNumbers} />
        </>
    );
};

export default CitationAnswer;
